import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Activity, ArrowDownRight, ArrowUpRight, Truck, FileText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/datetime";

const REF_LABEL = {
  stock_transfer_out: { label: "Transfer OUT", tone: "bg-red-50 text-red-700 border-red-200", icon: ArrowUpRight },
  stock_transfer_in:  { label: "Transfer IN",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ArrowDownRight },
  stock_transfer:     { label: "Transfer",     tone: "bg-blue-50 text-blue-700 border-blue-200", icon: Truck },
  sales_invoice:      { label: "Invoice",      tone: "bg-amber-50 text-amber-700 border-amber-200", icon: FileText },
  qc_pass:            { label: "QC Pass",      tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ArrowDownRight },
  qc_event:           { label: "QC",           tone: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: FileText },
};
const tone = (r) => REF_LABEL[r] || { label: r || "Movement", tone: "bg-gray-50 text-gray-700 border-gray-200", icon: Activity };

const REF_OPTS = [
  { value: "", label: "All movement types" },
  { value: "stock_transfer_out", label: "Transfer OUT" },
  { value: "stock_transfer_in",  label: "Transfer IN" },
  { value: "sales_invoice", label: "Sales Invoice" },
  { value: "qc_pass", label: "QC Pass" },
];

export default function StockMovements() {
  const [warehouses, setWarehouses] = useState([]);
  const [scope, setScope] = useState("warehouse");         // warehouse | branch | all
  const [locationId, setLocationId] = useState("");
  const [refType, setRefType] = useState("");
  const [refNo, setRefNo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/production/warehouses").then((r) => setWarehouses((r.data || []).filter((w) => w.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope, page: 1, page_size: 200 };
      if (locationId) params.location_id = locationId;
      if (refType) params.ref_type = refType;
      if (refNo) params.ref_no = refNo;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const r = await api.get("/sales/stock-movements", { params });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, [scope, locationId, refType, refNo, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setLocationId(""); setRefType(""); setRefNo(""); setDateFrom(""); setDateTo(""); setSearch("");
  };

  return (
    <div className="space-y-4">
      <PageSection
        title="Warehouse Stock Movements"
        description="Every stock IN and OUT across warehouses & branches — one row per transfer approval, invoice approval, cancellation or QC event. Drill by transfer #, invoice # or product."
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 h-9 rounded-md border border-gray-200 p-0.5 bg-gray-50">
              {[
                { v: "warehouse", label: "Warehouses" },
                { v: "branch", label: "Branches" },
                { v: "all", label: "All" },
              ].map((t) => (
                <button
                  key={t.v}
                  onClick={() => { setScope(t.v); setLocationId(""); }}
                  data-testid={`sm-scope-${t.v}`}
                  className={`px-3 rounded text-xs font-semibold transition-colors ${scope === t.v ? "bg-white text-[#9B1C1C] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
                >{t.label}</button>
              ))}
            </div>
            {scope === "warehouse" && (
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="sm-warehouse-filter">
                <option value="">All warehouses</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            )}
            <select value={refType} onChange={(e) => setRefType(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="sm-ref-filter">
              {REF_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" title="From date" data-testid="sm-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" title="To date" data-testid="sm-date-to" />
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Product / SKU / Ref #…" className="pl-8" data-testid="sm-search" />
            </div>
            <Input value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder="Ref # (exact)" className="h-9 w-36 font-mono" data-testid="sm-ref-no" />
            {(locationId || refType || refNo || dateFrom || dateTo || search) && (
              <button onClick={clearFilters} className="text-[11px] text-gray-500 hover:text-gray-900 underline flex items-center gap-1" data-testid="sm-clear">
                <RotateCcw className="h-3 w-3" /> Clear
              </button>
            )}
            <span className="text-xs text-gray-500 ml-auto">{loading ? "Loading…" : `${rows.length} of ${total.toLocaleString()} movement(s)`}</span>
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2 w-40">Date / Time</th>
                  <th className="px-3 py-2 w-28">Type</th>
                  <th className="px-3 py-2 w-32">Ref #</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2 text-right w-24">Qty Δ</th>
                  <th className="px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-14 text-center text-gray-400">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No movements match your filters.
                  </td></tr>
                )}
                {rows.map((r) => {
                  const t = tone(r.ref_type);
                  const Icon = t.icon;
                  const isNeg = Number(r.qty_delta || 0) < 0;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50" data-testid={`sm-row-${r.id}`}>
                      <td className="px-3 py-2 tabular-nums text-xs text-gray-600">{formatDateTime(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${t.tone}`}>
                          <Icon className="h-3 w-3" /> {t.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{r.ref_no || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-sm">{r.product_name || "—"}</div>
                        <div className="text-[11px] font-mono text-gray-500">{r.product_sku || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        <div>{r.location_name || "—"}</div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-widest">{r.scope}</div>
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold tabular-nums ${isNeg ? "text-red-700" : "text-emerald-700"}`}>
                        {isNeg ? "" : "+"}{r.qty_delta}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{r.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Surface>
      </PageSection>
    </div>
  );
}
